import { getTodayAtMidnight } from '@/features/dates/date-helpers';
import { z } from 'zod';

// RecReviewStatus enum schema
export const RecReviewStatusSchema = z.enum(['N', 'E', 'P', 'R', 'S'], {
	errorMap: () => ({
		message:
			'Invalid status. Must be one of: Review (R), Pending (P), No Change (N), Success (S), or Error (E)',
	}),
});

// Day of week selection schema
const dayOfWeekSchema = z.object({
	sunday: z.boolean(),
	monday: z.boolean(),
	tuesday: z.boolean(),
	wednesday: z.boolean(),
	thursday: z.boolean(),
	friday: z.boolean(),
	saturday: z.boolean(),
});

export const bulkRmRecsSchema = z
	.object({
		startDate: z.coerce
			.date()
			.min(getTodayAtMidnight(), 'Start date must be today or in the future'),
		endDate: z.coerce
			.date()
			.min(getTodayAtMidnight(), 'End date must be today or in the future'),
		rate: z.coerce.number().min(0).max(9999),
		notes: z.string().optional(),
		status: RecReviewStatusSchema,

		// Day of week selection - must have at least one day selected
		daysOfWeek: dayOfWeekSchema.refine(
			(days) => Object.values(days).some((day) => day === true),
			{
				message: 'At least one day of the week must be selected',
			},
		),
	})
	.refine(
		(data) => {
			return data.endDate >= data.startDate;
		},
		{
			message: 'End date must be after start date',
			path: ['endDate'],
		},
	);

export type BulkFormValues = z.infer<typeof bulkRmRecsSchema>;

export const daySelectionPresets = {
	weekdays: {
		sunday: true,
		monday: true,
		tuesday: true,
		wednesday: true,
		thursday: true,
		friday: false,
		saturday: false,
	},
	weekends: {
		sunday: false,
		monday: false,
		tuesday: false,
		wednesday: false,
		thursday: false,
		friday: true,
		saturday: true,
	},
	all: {
		sunday: true,
		monday: true,
		tuesday: true,
		wednesday: true,
		thursday: true,
		friday: true,
		saturday: true,
	},
	none: {
		sunday: false,
		monday: false,
		tuesday: false,
		wednesday: false,
		thursday: false,
		friday: false,
		saturday: false,
	},
} as const;
